import { useState, useEffect, useCallback, useRef } from 'react';
import { collection, onSnapshot, doc, updateDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db, addAuditLog } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/PageHeader';
import ToastNotification from '../../components/ToastNotification';
import styles from './TransactionsPage.module.css';
import { formatOrderId, formatTransactionId } from '../../utils/formatOrderId';

const TYPE_FILTERS = ['All', 'Cash', 'GCash', 'Partial Payment'];

function formatDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toISOString().slice(0, 10);
}

function formatAmount(amount) {
  if (typeof amount === 'number') return `₱${amount.toLocaleString()}`;
  return amount ?? '—';
}

function StatusBadge({ status }) {
  const cls = status === 'Verified' ? styles.badgeVerified
    : status === 'Settled'  ? styles.badgeSettled
    : styles.badgePending;
  return <span className={`${styles.badge} ${cls}`}>{status}</span>;
}

function TypeBadge({ type }) {
  const cls = type === 'Cash' ? styles.badgeCash
    : type === 'GCash' ? styles.badgeGcash
    : styles.badgePartialPayment;
  return <span className={`${styles.badge} ${cls}`}>{type}</span>;
}

export default function TransactionsPage() {
  const { user }                         = useAuth();
  const [rows, setRows]                  = useState([]);
  const [search, setSearch]              = useState('');
  const [typeFilter, setTypeFilter]      = useState('All');
  const [toast, setToast]                = useState(null);
  const [dueDateTx, setDueDateTx]        = useState(null);
  const [dueDate, setDueDate]            = useState('');
  const dueDateRef                       = useRef(null);

  const dismissToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    let orders = [];
    let txMap  = {};

    function merge() {
      const merged = orders.map(order => ({ ...order, tx: txMap[order._docId] ?? null }));
      setRows(merged);
    }

    const unsubOrders = onSnapshot(
      query(collection(db, 'orders'), orderBy('createdAt', 'desc')),
      snap => {
        orders = snap.docs.map(d => ({ _docId: d.id, ...d.data() }));
        merge();
      }
    );

    const unsubTx = onSnapshot(collection(db, 'transactions'), snap => {
      txMap = {};
      snap.docs.forEach(d => { txMap[d.data().orderId] = { _txDocId: d.id, ...d.data() }; });
      merge();
    });

    return () => { unsubOrders(); unsubTx(); };
  }, []);

  const filtered = rows.filter(row => {
    const q = search.toLowerCase();
    const matchesSearch = !q
      || (row._docId || '').toLowerCase().includes(q)
      || (row.branchName || '').toLowerCase().includes(q)
      || (row.tx?._txDocId || '').toLowerCase().includes(q);
    const matchesType = typeFilter === 'All' || (row.tx?.type ?? '') === typeFilter;
    return matchesSearch && matchesType;
  });

  async function handleVerify(tx) {
    try {
      await updateDoc(doc(db, 'transactions', tx._txDocId), {
        status:    'Verified',
        updatedAt: serverTimestamp(),
      });
      await addAuditLog(user.uid, user.name, `Verified transaction ${tx._txDocId}`, 'Transactions');
      setToast({ message: `Transaction ${tx._txDocId} marked as verified.`, type: 'success' });
    } catch (e) {
      console.error('Verify failed:', e);
      setToast({ message: 'Failed to verify transaction.', type: 'error' });
    }
  }

  async function handleSetDueDate() {
    if (!dueDate || !dueDateTx) return;
    try {
      await updateDoc(doc(db, 'transactions', dueDateTx._txDocId), {
        paymentDue: dueDate,
        updatedAt:  serverTimestamp(),
      });
      await addAuditLog(user.uid, user.name, `Set due date ${dueDate} for transaction ${dueDateTx._txDocId}`, 'Transactions');
      setToast({ message: `Due date set for transaction ${dueDateTx._txDocId}.`, type: 'success' });
    } catch (e) {
      console.error('Set due date failed:', e);
      setToast({ message: 'Failed to set due date.', type: 'error' });
    } finally {
      setDueDateTx(null);
      setDueDate('');
    }
  }

  async function handleSettle(tx) {
    try {
      await updateDoc(doc(db, 'transactions', tx._txDocId), {
        status:    'Settled',
        updatedAt: serverTimestamp(),
      });
      await addAuditLog(user.uid, user.name, `Marked transaction ${tx._txDocId} as Settled`, 'Transactions');
      setToast({ message: `Transaction ${tx._txDocId} marked as Settled.`, type: 'success' });
    } catch (e) {
      console.error('Settle failed:', e);
      setToast({ message: 'Failed to settle transaction.', type: 'error' });
    }
  }

  const withTx       = rows.filter(r => r.tx);
  const totalCount   = withTx.length;
  const cashCount    = withTx.filter(r => r.tx.type === 'Cash').length;
  const gcashCount   = withTx.filter(r => r.tx.type === 'GCash').length;
  const partialCount = withTx.filter(r => r.tx.type === 'Partial Payment').length;

  return (
    <div className={styles.page}>
      <PageHeader title="Transactions" description="Review and verify branch payments" />

      <div className={styles.statsRow}>
        <div className={styles.statCard}><span className={styles.statValue}>{totalCount}</span><span className={styles.statLabel}>Total Transactions</span></div>
        <div className={styles.statCard}><span className={styles.statValue}>{cashCount}</span><span className={styles.statLabel}>Cash Payments</span></div>
        <div className={styles.statCard}><span className={styles.statValue}>{gcashCount}</span><span className={styles.statLabel}>GCash Payments</span></div>
        <div className={styles.statCard}><span className={styles.statValue}>{partialCount}</span><span className={styles.statLabel}>Partial Payment</span></div>
      </div>

      <div className={styles.searchWrap}>
        <svg className={styles.searchIcon} viewBox="0 0 20 20" fill="none">
          <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.8"/>
          <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        </svg>
        <input className={styles.searchInput} type="text" placeholder="Search by Transaction ID or Branch…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div className={styles.filterRow}>
        {TYPE_FILTERS.map(f => (
          <button key={f} className={`${styles.pill} ${typeFilter === f ? styles.pillActive : ''}`} onClick={() => setTypeFilter(f)}>{f}</button>
        ))}
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Transaction ID</th><th>Order ID</th><th>Branch</th>
              <th>Amount</th><th>Type</th><th>Ref No.</th><th>Date</th><th>Due Date</th><th>Status</th><th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={10} className={styles.empty}>No transactions match your search.</td></tr>
            ) : (
              filtered.map((row, idx) => {
                const tx = row.tx;
                return (
                  <tr key={row._docId} className={idx % 2 === 0 ? styles.rowEven : styles.rowOdd}>
                    <td className={styles.txId}>{tx ? formatTransactionId(tx._txDocId, tx.createdAt) : '—'}</td>
                    <td className={styles.orderId}>{formatOrderId(row._docId)}</td>
                    <td>{row.branchName}</td>
                    <td className={styles.amount}>
                      {formatAmount(tx ? tx.amount : row.total)}
                      {tx?.type === 'Partial Payment' && tx.remainingAmount > 0 && (
                        <div className={styles.balanceSub}>Remaining: {formatAmount(tx.remainingAmount)}</div>
                      )}
                    </td>
                    <td>{tx ? <TypeBadge type={tx.type} /> : '—'}</td>
                    <td className={styles.refNumber}>
                      {tx?.gcashRefNumber ?? '—'}
                      {tx?.balanceGcashRef && (
                        <div className={styles.balanceSub} title="Balance payment GCash ref">Bal. ref: {tx.balanceGcashRef}</div>
                      )}
                    </td>
                    <td>{formatDate(row.createdAt)}</td>
                    <td className={styles.dueDate}>{tx?.paymentDue ?? '—'}</td>
                    <td><StatusBadge status={tx?.status ?? row.status} /></td>
                    <td>
                      {!tx ? (
                        <span className={styles.verifiedText}>—</span>
                      ) : tx.status === 'Pending' ? (
                        <button className={styles.verifyBtn} onClick={() => handleVerify(tx)}>Verify</button>
                      ) : tx.status === 'Verified' && tx.type === 'Partial Payment' && !tx.paymentDue ? (
                        <button className={styles.setDateBtn} onClick={() => { setDueDateTx(tx); setDueDate(''); }}>Set Due Date</button>
                      ) : tx.status === 'Verified' && tx.type === 'Partial Payment' && tx.paymentDue ? (
                        <button className={styles.settleBtn} onClick={() => handleSettle(tx)}>Mark Settled</button>
                      ) : (
                        <span className={styles.verifiedText}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {toast && <ToastNotification message={toast.message} type={toast.type} onDismiss={dismissToast} />}

      {dueDateTx && (
        <div className={styles.overlay} onClick={() => setDueDateTx(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Set Payment Due Date</h3>
            <p className={styles.modalBody}>
              Transaction <strong>{dueDateTx._txDocId}</strong> — Remaining balance: <strong>{formatAmount(dueDateTx.remainingAmount)}</strong>.<br />
              Choose the date by which the branch must settle the remaining balance.
            </p>
            <input
              ref={dueDateRef}
              type="date"
              className={styles.dateInput}
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
            />
            <div className={styles.modalActions}>
              <button className={styles.cancelBtn} onClick={() => setDueDateTx(null)}>Cancel</button>
              <button className={styles.saveBtn} onClick={handleSetDueDate} disabled={!dueDate}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
